import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_API_URL || '';

function LoadingBar({ active }) {
  if (!active) return null;
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 3, zIndex: 9999, overflow: 'hidden', background: 'rgba(37,99,235,0.15)' }}>
      <div style={{
        height: '100%', background: 'linear-gradient(90deg, #2563eb, #60a5fa, #2563eb)',
        backgroundSize: '200% 100%',
        animation: 'loadingBar 1.2s ease-in-out infinite',
        borderRadius: 2
      }} />
    </div>
  );
}

// Read the auth token from whichever storage the user last chose. When
// they check "This is a public device" on login, we write to
// sessionStorage instead of localStorage so the token evaporates the
// moment the browser tab closes.
function readTokenFromStorage() {
  return sessionStorage.getItem('token') || localStorage.getItem('token') || null;
}

function writeTokenToStorage(token, publicDevice) {
  // Wipe both stores first so we never keep a stale token in the other
  // one after the user switches modes.
  sessionStorage.removeItem('token');
  localStorage.removeItem('token');
  if (!token) return;
  if (publicDevice) sessionStorage.setItem('token', token);
  else localStorage.setItem('token', token);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(readTokenFromStorage());
  const [companyCode, setCompanyCodeState] = useState(localStorage.getItem('company_code') || '');
  const [companyName, setCompanyNameState] = useState(localStorage.getItem('company_name') || '');
  const [loading, setLoading] = useState(true);
  const [discoveringTenant, setDiscoveringTenant] = useState(true);
  const [maintenance, setMaintenance] = useState(null);
  // Central-hub disabled gate. Populated when any /api/* response comes
  // back as 423 server_disabled or when the /api/server-status poll
  // reports enabled:false. App.js short-circuits to <DisabledScreen> when
  // this is set, so the user can't continue using a disabled server.
  const [serverDisabled, setServerDisabled] = useState(null); // { message, portalUrl, reason } | null
  const [fetchCount, setFetchCount] = useState(0);
  const fetchTimer = useRef(null);

  useEffect(() => {
    if (token) {
      const headers = { Authorization: `Bearer ${token}` };
      if (companyCode) headers['X-Tenant-Code'] = companyCode;
      fetch(`${API}/api/auth/verify`, { headers, credentials: 'include' })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(data => { setUser(data.user); setLoading(false); })
        .catch(() => { logout(); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, [token]);

  // Ask the server who we are. It resolves the code from the URL
  // (subdomain / /t/<code>/ prefix) or the tenant_code cookie and returns
  // the identity. If none is found we land on the CompanyCode entry page.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/tenant/whoami`, { credentials: 'include' })
      .then(r => r.json().catch(() => null))
      .then(data => {
        if (cancelled) return;
        if (data && data.ok && data.company_code) {
          setCompanyCode(data.company_code, data.server_name || data.company || '');
        } else if (companyCode) {
          // Stored code didn't resolve — drop it so the user sees the prompt.
          setCompanyCode('', '');
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setDiscoveringTenant(false); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  const setCompanyCode = (code, name) => {
    const clean = (code || '').trim();
    if (clean) {
      localStorage.setItem('company_code', clean);
      setCompanyCodeState(clean);
    } else {
      localStorage.removeItem('company_code');
      setCompanyCodeState('');
    }
    if (name != null) {
      if (name) { localStorage.setItem('company_name', name); setCompanyNameState(name); }
      else { localStorage.removeItem('company_name'); setCompanyNameState(''); }
    }
  };

  useEffect(() => {
    if (!companyCode) return;
    const checkMaintenance = () => {
      const headers = { 'X-Tenant-Code': companyCode };
      fetch(`${API}/api/maintenance/status`, { headers, credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setMaintenance(data); })
        .catch(() => {});
    };
    checkMaintenance();
    const interval = setInterval(checkMaintenance, 30000);
    return () => clearInterval(interval);
  }, [companyCode]);

  const login = async (email, password, username = null, authMethod = null, opts = {}) => {
    const body = { password };
    if (username) {
      body.username = username;
      body.auth_method = 'ad';
    } else {
      body.email = email;
      body.auth_method = authMethod || 'local';
    }
    const headers = { 'Content-Type': 'application/json' };
    if (companyCode) headers['X-Tenant-Code'] = companyCode;
    const res = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Login failed');
    }
    const data = await res.json();
    // OTP gate — password was accepted but the tenant + user opted in
    // to one-time codes. The Login page handles the second-factor
    // screen; we hand the challenge token back for it to finish with.
    if (data.otp_required) return data;
    finaliseSession(data, !!opts.publicDevice);
    return data;
  };

  // Second-step OTP verification: exchanges the challenge token + code
  // for a real session token. Shares finalisation with plain login.
  const verifyOtp = async (otpToken, code, opts = {}) => {
    const headers = { 'Content-Type': 'application/json' };
    if (companyCode) headers['X-Tenant-Code'] = companyCode;
    const res = await fetch(`${API}/api/auth/verify-otp`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ otp_token: otpToken, code }),
    });
    if (!res.ok) {
      const err = await res.json();
      const e = new Error(err.error || 'Verification failed');
      e.status = res.status;
      throw e;
    }
    const data = await res.json();
    finaliseSession(data, !!opts.publicDevice);
    return data;
  };

  const resendOtp = async (otpToken) => {
    const headers = { 'Content-Type': 'application/json' };
    if (companyCode) headers['X-Tenant-Code'] = companyCode;
    const res = await fetch(`${API}/api/auth/otp-resend`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ otp_token: otpToken }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Resend failed');
    }
    return res.json();
  };

  const finaliseSession = (data, publicDevice) => {
    writeTokenToStorage(data.token, publicDevice);
    setToken(data.token);
    setUser(data.user);
    if (data.user?.company_code && data.user.company_code !== companyCode) {
      setCompanyCode(data.user.company_code, data.user.company || null);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    setToken(null);
    setUser(null);
    fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
  };

  // `silent: true` skips the global LoadingBar for background polls (badge
  // counts, auto-refresh timers) — otherwise every 15-60s poll flashes the
  // same indicator meant for real user-triggered actions.
  const authFetch = useCallback((url, options = {}) => {
    const { silent, ...fetchOptions } = options;
    const headers = { ...fetchOptions.headers, Authorization: `Bearer ${token}` };
    if (companyCode) headers['X-Tenant-Code'] = companyCode;
    if (fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    let shown = false;
    const timer = silent ? null : setTimeout(() => { shown = true; setFetchCount(c => c + 1); }, 300);
    return fetch(`${API}${url}`, { ...fetchOptions, headers, credentials: 'include' })
      .then(async (res) => {
        // Central-hub disabled gate: server returns 423 for every /api call
        // when the tenant is disabled. Peek the body without consuming it
        // (clone first), then surface the payload to the global state.
        if (res.status === 423) {
          try {
            const body = await res.clone().json();
            if (body && body.error === 'server_disabled') {
              setServerDisabled({ message: body.message, portalUrl: body.portalUrl, reason: body.reason });
            }
          } catch { /* not JSON — ignore */ }
        }
        return res;
      })
      .finally(() => {
        if (timer) clearTimeout(timer);
        if (shown) setFetchCount(c => Math.max(0, c - 1));
      });
  }, [token, companyCode]);

  // Poll the central-hub-backed server-status endpoint once we know the
  // company code. Cheap (60s cadence) and the server caches for 15s, so
  // a disabled flip in the hub is reflected here within ~75s without
  // needing the user to make another /api call.
  useEffect(() => {
    if (!companyCode) return;
    let cancelled = false;
    const tick = () => {
      fetch(`${API}/api/server-status?code=${encodeURIComponent(companyCode)}`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (cancelled || !data) return;
          if (data.enabled === false) {
            setServerDisabled({ message: data.message, portalUrl: data.portalUrl, reason: data.reason });
          } else {
            setServerDisabled(null);
          }
        })
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [companyCode]);

  const updateUser = (newUser) => {
    setUser(newUser);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading: loading || discoveringTenant, login, verifyOtp, resendOtp, logout, authFetch, updateUser, maintenance, serverDisabled, companyCode, companyName, setCompanyCode }}>
      <LoadingBar active={fetchCount > 0} />
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
